import { defineStore } from 'pinia'
import { PAGE_PARAMS } from '../pages'
import http from '../helpers/http-call'
import { ENDPOINT_URL, getDataLang, getQuery } from '../helpers/api'
import { useAuthStore } from '../auth'

interface IQueryProps {
  showData?: boolean
  category?: string
  headers?: {
    Authorization?: string
  }
  hotel_id?: number
  lang_id?: number
  page_size?: number
  page?: number
}
export interface IRoomsData {
  rooms: IRooms[]
}
export interface IRoomsRate {
  rooms: IRooms[]
}
export interface IRooms {
  room_id: string
  room_slug: string
  room_title: string | null
  room_desc: string | null
  room_rate: string | number | null
  room_images: Array<IRoomImages>
  sb_id: string | null
  room_status: string | null
  facilities_icon: Array<IRoomFacilities>
}

export interface IRoomImages {
  image_url: string | null
  is_placeholder_image?: boolean
}

export interface IRoomFacilities {
  preset_facility_name: string | null
  icon_label: string | null
  icon_image: string | null
  icon_text: string | null
}

export interface IRoomDetail {
  room_id: string
  room_slug: string
  room_title: string | null
  room_desc: string | null
  room_rate: string | number | null
  room_images: Array<IRoomImages>
  sb_id: string | null
  room_status: string | null
  facilities_icon: Array<IRoomFacilities>
}

interface IQueryDetailProps {
  headers: any
  room_slug?: string
}

export interface IRoomsState {
  list: IRoomsData
  data: IRoomsData
  rate: Object
  dataSwiper: IRoomsData
  detail: IRoomDetail
  loadingDetail: boolean
  loading: boolean
  params?: IQueryProps
}

const DATA = {
  rooms: [],
}

const DEFAULT_DETAIL = {
  room_id: '',
  room_slug: '',
  room_title: null,
  room_desc: null,
  room_rate: null,
  room_images: [],
  sb_id: null,
  room_status: null,
  facilities_icon: [],
}

const RATE = {}

export const useRoomsStore = defineStore('rooms', {
  state: (): IRoomsState => ({
    list: DATA,
    loading: false,
    loadingDetail: false,
    detail: DEFAULT_DETAIL,
    data: DATA,
    rate: RATE,
    dataSwiper: DATA,
    params: PAGE_PARAMS,
  }),
  getters: {
    getData(): IRoomsState['data'] {
      const params: IQueryProps = getQuery()
      return getDataLang(this.data, Number(params.lang_id), 'rooms')
    },
    getList(): IRoomsState['list'] {
      const params: IQueryProps = getQuery()
      return getDataLang(this.list, Number(params.lang_id), 'rooms')
    },
    getDataSwiper(): IRoomsState['data'] {
      const params: IQueryProps = getQuery()
      return getDataLang(this.dataSwiper, Number(params.lang_id), 'rooms')
    },
    getDetail(state): any {
      const list = getDataLang(state.data?.rooms)
      if (!list)
        return DATA

      return (slug: string) =>
        Array.isArray(list) && list.length ? list.find((el: any) => el.room_slug === slug) : DATA
    },
  },
  actions: {
    setList(data: IRoomsData, isData?: boolean) {
      this.list = data
      if (isData)
        this.data = data
    },
    async setDetail(query: IQueryDetailProps) {
      const { room_slug, headers }: IQueryDetailProps = query
      const params = getQuery()
      let data = DEFAULT_DETAIL
      let response: any = {
        data: {},
      }
      if (params?.lang_id)
        delete params.lang_id

      if (!this.loadingDetail) {
        this.loadingDetail = true
        response = await http.call({
          url: `${ENDPOINT_URL}/room/${room_slug}`,
          headers,
          params,
          method: 'GET',
          data: null,
        })
      }

      this.loadingDetail = false
      if (response?.data?.data)
        data = getDataLang(response?.data?.data, 0, 'rooms')

      this.detail = data
    },
    async setRate(query: IQueryProps, id?: string | undefined) {
      const { headers }: IQueryProps = query
      if (!this.data.rooms || this.data.rooms.length === 0) {
        console.warn('[pinia-rooms] err rooms')
        return
      }

      const { hotel_id } = getQuery()
      const useAuth = useAuthStore()
      let data = []
      const params = {
        hotel_id,
      }
      let response: any = {
        data: {},
      }
      if (!this.loading) {
        this.loading = true
        response = await http.call({
          url: `${ENDPOINT_URL}/room-rate`,
          headers,
          params,
          method: 'GET',
          data: null,
        })
      }
      this.loading = false
      this.params = params
      if (response?.response?.data?.errorCode === 401) {
        const auth = await useAuth?.setData({ showData: true })
        if (auth?.token) {
          response = await http.call({
            url: `${ENDPOINT_URL}/room-rate`,
            headers: {
              Authorization: `Bearer ${auth.token}`,
            },
            params,
            method: 'GET',
            data: null,
          })
        }
      }
      if (response?.data?.data?.rooms)
        data = response.data?.data?.rooms

      if (data && typeof data === 'object' && Object.keys(data).length) {
        const keys = Object.keys(data)
        const values = Object.values(data)
        if (id) {
          const index: number = keys.findIndex((obj: string) => obj === id)
          if (values[index])
            return values[index]
        }
        else {
          const tmp = this.data.rooms
          for (let i = 0; i < this.data.rooms.length; i++) {
            const el = this.data.rooms[i]
            const index: number = keys.findIndex((obj: string) => obj === el.room_slug)
            if (values[index])
              tmp[i].room_rate = values[index]
          }
          this.data.rooms = tmp
          if (this.list.rooms)
            this.list.rooms = tmp
        }
      }
    },
    async setData(query: IQueryProps, isSwiper: boolean | undefined = false) {
      const { showData, headers }: IQueryProps = query
      const params = getQuery()
      const useAuth = useAuthStore()
      let data = DATA
      let response: any = {
        data: {},
      }

      if (params?.lang_id)
        delete params.lang_id

      // if (!this.loading) {
      this.loading = true
      response = await http.call({
        url: `${ENDPOINT_URL}/rooms`,
        headers,
        params,
        method: 'GET',
        data: null,
      })
      // }
      this.params = params
      if (response?.response?.data?.errorCode === 401) {
        const auth = await useAuth?.setData({ showData: true })
        if (auth?.token) {
          response = await http.call({
            url: `${ENDPOINT_URL}/rooms`,
            headers: {
              Authorization: `Bearer ${auth.token}`,
            },
            params,
            method: 'GET',
            data: null,
          })
        }
      }
      if (response?.data?.data)
        data = getDataLang(response?.data?.data, 0, 'rooms')

      if (isSwiper)
        this.dataSwiper = data
      else this.data = data

      setTimeout(() => {
        this.loading = false
      }, 1000)
      if (showData)
        return await data
    },
  },
})
